import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import {
  COURTESY_GRANT_CATEGORIES,
  type LifetimeFreeCategory,
} from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { applyLifetimeFreeCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
};

/**
 * `details` do erro do `applyLifetimeFree` quando o gateway não cancelou a
 * assinatura paga (#795, BIL-2). `null` para qualquer outro erro.
 *
 * Origem: #795 — 2026-10-03 19:08 +0200 (auditoria, 2ª rodada, BIL-2).
 *
 * Usado por: `submit` do `ApplyLifetimeFreeDialog` (este arquivo).
 *
 * Dados — `FunctionsError.details` (3º argumento do `HttpsError` do servidor,
 * `apps/functions/src/admin/applyLifetimeFree.ts`), código `aborted`:
 *   { "code": "gateway_cancel_failed", "provider": "stripe" | "asaas",
 *     "courtesy_written": true }   // Stripe: cortesia gravada, assinatura viva
 *   { …, "courtesy_written": false } // Asaas: nada gravado, pode repetir
 */
function gatewayCancelFailureOf(err: unknown): { provider: string; courtesyWritten: boolean } | null {
  if (typeof err !== 'object' || err === null) return null;
  const details = (err as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) return null;
  const d = details as Record<string, unknown>;
  if (d.code !== 'gateway_cancel_failed') return null;
  return {
    provider: d.provider === 'asaas' ? 'Asaas' : 'Stripe',
    courtesyWritten: d.courtesy_written === true,
  };
}

/**
 * Dialog que concede cortesia (isenção) a um tenant de venda direta via
 * callable `applyLifetimeFree`.
 *
 * Origem: sem issue — commit 5af121d4c, 2026-05-25 23:10 +0200 (Sprint 35,
 * PR G3). Desde a F7 da #679 (#738) a cortesia é tipo comercial
 * (`commercial_mode: COURTESY`), sem cupom; se havia assinatura paga, o
 * servidor a cancela no gateway dono dela.
 *
 * Contrato: chama `applyLifetimeFreeCallable`; sucesso → toast + fecha. Erro
 * `gateway_cancel_failed` (`details.code`) vira aviso traduzido: com
 * `courtesy_written` (Stripe falhou depois de gravar) o dialog FECHA — a
 * cortesia já vale e repetir daria `already_not_direct` — e o aviso diz que a
 * assinatura pode seguir cobrando; sem ele (Asaas, nada gravado) fica aberto
 * para repetir. Demais erros: `err.message`. Formulário: RadioGroup com as
 * categorias; `reason` obrigatório quando `category === 'OTHER'`; AdminConfirm
 * antes do submit reforça que a operação só se reverte via
 * `removeLifetimeFreeCallable`. O servidor exige
 * `ADMIN_PERMISSIONS.manageCourtesy` (ROOT/FINANCE; staff only).
 *
 * Usado por: `BillingActions` → `routes/TenantDetailPage.tsx` (rota
 * `/tenants/:id`, menu Tenants), admin web. Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Falhas conhecidas:
 *  - 2026-10-03 (#795, BIL-2): a falha de cancelamento chegava como texto cru
 *    em pt-BR, sem distinguir "cortesia gravada e gateway cobrando" de "nada
 *    gravado".
 *
 * Histórico:
 *  - 2026-10-03 18:40 +0200 — (#795, BIL-2) aviso traduzido para
 *    `gateway_cancel_failed`.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, permissão; seção "UX" fundida no
 *    Contrato (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function ApplyLifetimeFreeDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [category, setCategory] = useState<LifetimeFreeCategory>('FOUNDER');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCategory('FOUNDER');
    setReason('');
    setConfirmOpen(false);
    setError(null);
  }, [open]);

  function close(): void {
    setError(null);
    onClose();
  }

  /** Valida o motivo (obrigatório em OTHER) antes de abrir o `AdminConfirm`. */
  function requestConfirm(): void {
    if (category === 'OTHER' && reason.trim().length === 0) {
      setError(t('billing.lifetimeFree.apply.errors.reasonRequiredOther'));
      return;
    }
    setError(null);
    setConfirmOpen(true);
  }

  /**
   * Concede a cortesia via `applyLifetimeFree` (depois do `AdminConfirm`).
   *
   * Origem: sem issue — commit 5af121d4c, 2026-05-25 23:10 +0200.
   *
   * Contrato: input `{ "tenantId": "tenant-1", "category": "FOUNDER",
   * "reason": "" }` (`reason` sempre enviado, vazio permitido fora de OTHER).
   * Três saídas de erro: `gateway_cancel_failed` com `courtesy_written` →
   * toast persistente e FECHA (o modal some, então o toast é visível); sem
   * `courtesy_written` → erro e fica aberto; resto → `err.message`. Esses dois
   * últimos aparecem num `Alert` dentro do modal principal (#1021 — o toast
   * ficava atrás do overlay). Efeito no servidor: `commercial_mode: COURTESY` em
   * `billing/subscription` e, se havia assinatura paga, cancelamento no gateway.
   *
   * Usado por: `AdminConfirm` deste diálogo.
   *
   * Falhas conhecidas: ver a docstring do componente (#795, BIL-2).
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await applyLifetimeFreeCallable({ tenantId, category, reason: reason.trim() });
      toast.success(t('billing.lifetimeFree.apply.success'));
      setConfirmOpen(false);
      close();
    } catch (err) {
      setConfirmOpen(false);
      // #795 BIL-2 (2026-10-03 18:40 +0200): a falha de cancelamento no
      // gateway tem contrato próprio (`details.code`). Com a cortesia já
      // gravada, fechar: repetir seria recusado (`already_not_direct`) e o
      // operador precisa saber que o gateway pode seguir cobrando.
      const gatewayFailure = gatewayCancelFailureOf(err);
      if (gatewayFailure?.courtesyWritten === true) {
        toast.error(
          t('billing.lifetimeFree.apply.errors.gatewayCancelFailedWritten', { provider: gatewayFailure.provider }),
          // Persistente (revisão #795): o diálogo fecha junto e um toast que
          // some em 8 s passaria despercebido — o gateway pode seguir cobrando.
          { duration: Infinity },
        );
        close();
        return;
      }
      if (gatewayFailure !== null) {
        setError(
          t('billing.lifetimeFree.apply.errors.gatewayCancelFailedNothingWritten', {
            provider: gatewayFailure.provider,
          }),
        );
        return;
      }
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        visible={open && !confirmOpen}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(submitting)) { close(); } }}
        header={t('billing.lifetimeFree.apply.title', { tenantName })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={close} disabled={submitting}>
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button variant="primary" onClick={requestConfirm} disabled={submitting}>
                {t('billing.lifetimeFree.apply.applyButton')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p" color="text-body-secondary">
            {t('billing.lifetimeFree.apply.description')}
          </Box>

          <FormField label={t('billing.lifetimeFree.apply.categoryLabel')}>
            <RadioGroup
              value={category}
              onChange={({ detail }): void => {
                setCategory(detail.value as LifetimeFreeCategory);
                // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
                setError(null);
              }}
              items={COURTESY_GRANT_CATEGORIES.map((cat) => ({
                value: cat,
                label: t(`billing.lifetimeFree.category.${cat}`, { defaultValue: cat }),
              }))}
            />
          </FormField>

          <FormField
            label={t('billing.lifetimeFree.apply.reasonLabel')}
            constraintText={category === 'OTHER' ? '*' : undefined}
          >
            <Input
              value={reason}
              onChange={({ detail }): void => {
                setReason(detail.value);
                // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
                setError(null);
              }}
              placeholder={t('billing.lifetimeFree.apply.reasonPlaceholder')}
            />
          </FormField>
          {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
              do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('billing.lifetimeFree.apply.confirmTitle')}
        confirmLabel={t('billing.lifetimeFree.apply.confirmApply')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">
          {t('billing.lifetimeFree.apply.confirmDescription', {
            tenantName,
            category: t(`billing.lifetimeFree.category.${category}`, { defaultValue: category }),
          })}
        </Box>
      </AdminConfirm>
    </>
  );
}
